import { useRef } from 'react';
import {
  Box,
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateTitle,
  Stack,
  Tooltip,
} from '@customerio/pluma-components/react';
import { FlowCards } from './flow-cards';
import { PlanInfo } from './plan-info';
import type { PlanDetails } from '../../use-plan-details';

type ObjectsLandingPageProps = {
  canConfigure: boolean;
  createHref: string;
  planDetails: PlanDetails | null;
};

export function ObjectsLandingPage({ canConfigure, createHref, planDetails }: ObjectsLandingPageProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <Box as="section" data-test-objects-intro-section>
      <Stack gap="400" alignInline="center" mt="400">
        <EmptyState variant="promotion">
          <EmptyStateTitle id="objects-landing-page" data-test-objects-landing-page-title>
            Your data, in your words.
          </EmptyStateTitle>
          <EmptyStateDescription>
            Create, associate, and utilize the data that matters most to your business and customers – like companies,
            opportunities, events, and more. {planDetails != null ? <PlanInfo planDetails={planDetails} /> : null}
          </EmptyStateDescription>
          <EmptyStateActions>
            <Tooltip
              isDisabled={canConfigure}
              isCustom={true}
              content="Contact an Admin or Workspace Manager to configure this feature."
            >
              {(context) => (
                <Button
                  ref={context.reference.setReference}
                  {...context.reference.getReferenceProps()}
                  data-test-objects-landing-page-cta="create_object_type"
                  isDisabled={!canConfigure}
                  variant="primary"
                  href={createHref}
                  icon="chevron-right"
                  iconPosition="trailing"
                >
                  Create object type
                </Button>
              )}
            </Tooltip>
            <Button
              data-test-objects-landing-page-documentation-url
              href="https://docs.customer.io/objects/"
              isExternal={true}
              icon="exit"
              iconPosition="trailing"
              variant="secondary"
            >
              Learn more
            </Button>
          </EmptyStateActions>
        </EmptyState>
      </Stack>

      <FlowCards containerRef={containerRef} />
    </Box>
  );
}
